import { Box, List, Theme, Toolbar, useMediaQuery } from '@mui/material';
import AppBar from '@/components/AppBar';
import Submenu from './nav/menu/Submenu';
import PaymentFailedAlert from './PaymentFailedAlert';
import { usePayment } from '@/providers/payment-provider';
import { submenuWidth } from './nav/MainNav';
import EmailNotValidatedAlert from './EmailNotValidatedAlert';
import { useQuery } from '@apollo/client';
import { HasEmailValidatedDocument } from '@/graphql/generated/has-email-validated.generated';
import { matchPath, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { useAndie } from '@/providers/andie-provider';
import { useMenu } from '@/providers/menu-provider';
import { HEADER_HEIGHT } from '@/constants';

const PageLayout = ({
    children,
    boxMargin,
    legacyIframe,
    toolBarOptions,
    hasFixedSubheader,
}: Readonly<{
    children: React.ReactNode;
    boxMargin?: string;
    legacyIframe?: boolean;
    toolBarOptions?: React.ReactNode;
    hasFixedSubheader?: boolean;
}>) => {
    const { menu } = useMenu();
    const pathname = useLocation().pathname;
    const submenu = menu.find((each) => !!matchPath({ path: each.path, end: false }, pathname));
    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const { hasFailedPayment, onClose } = usePayment();
    const theme = useTheme();
    const { data } = useQuery(HasEmailValidatedDocument);
    const { open: andieOpen } = useAndie();

    const hasEmailValidated = !data || data.hasEmailValidated;
    return (
        <Box width="100vw" minHeight="100vh" height="100%" bgcolor="background.paper-elevation-4">
            <AppBar optionsComponent={toolBarOptions} />
            <Toolbar variant="dense" />
            <Box display="flex">
                {!isMobile && submenu?.sub && (
                    <Box
                        width="100%"
                        maxWidth={submenuWidth}
                        minHeight={`calc(100vh - ${HEADER_HEIGHT}px)`}
                        bgcolor="background.paper"
                        borderRight="1px solid"
                        borderColor="elevation.outline-variant"
                        px={1}
                        position="fixed"
                    >
                        <List>
                            <Submenu menuSelected={submenu.path} />
                        </List>
                    </Box>
                )}
                <Box
                    component="main"
                    m={boxMargin === undefined ? (isMobile ? 1.5 : 3) : boxMargin}
                    flexGrow={1}
                    // Flex items default to `min-width: auto`, so `main` refused to shrink below the
                    // min-content width of whatever it was rendering. While the page loads that is
                    // briefly wider than a phone, and `main` pushed the whole document sideways.
                    minWidth={0}
                    {...(!isMobile &&
                        submenu?.sub && {
                            ml: legacyIframe
                                ? `${submenuWidth}px`
                                : `calc(${theme.spacing(3)} + ${submenuWidth}px)`,
                        })}
                    sx={(theme) => ({
                        ...(andieOpen && {
                            [theme.breakpoints.down('sm')]: {
                                visibility: 'hidden',
                            },
                        }),
                    })}
                    {...(legacyIframe && {
                        display: 'flex',
                        flexDirection: 'column',
                        height: `calc(100vh - ${HEADER_HEIGHT}px - 1px)`,
                        mt: '1px',
                    })}
                    {...(!legacyIframe && {
                        height: '100%',
                    })}
                    {...(hasFixedSubheader && {
                        mt: 11,
                    })}
                >
                    {hasFailedPayment ? (
                        <Box
                            mb={{ xs: 1.5, sm: 3 }}
                            {...(legacyIframe && {
                                m: { xs: 1.5, sm: 3 },
                            })}
                        >
                            <PaymentFailedAlert onClose={onClose} />
                        </Box>
                    ) : !hasEmailValidated ? (
                        <Box
                            mb={{ xs: 1.5, sm: 3 }}
                            {...(legacyIframe && {
                                m: { xs: 1.5, sm: 3 },
                            })}
                        >
                            <EmailNotValidatedAlert />
                        </Box>
                    ) : null}
                    {children}
                </Box>
            </Box>
        </Box>
    );
};

export default PageLayout;
